import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { requestInterpretation } from '../lib/api';
import { stepMs } from '../lib/constants';
import { startDictation, type DictationSession } from '../lib/speech';
import type { Dialect, Mode, Speed } from '../lib/types';
import { initialState, reducer, type State } from './reducer';

let sequence = 0;
const nextId = (prefix: string): string => `${prefix}${Date.now().toString(36)}${(sequence += 1)}`;

export interface Signpost {
  state: State;
  setMode(mode: Mode): void;
  setSpeed(speed: Speed): void;
  setDialect(dialect: Dialect): void;
  setGlossScale(scale: number): void;
  setEnglishOpen(open: boolean): void;
  toggleEnglishPanel(): void;
  toggleMessageEnglish(id: string): void;
  openSettings(): void;
  closeSettings(): void;
  setDraft(draft: string): void;
  togglePlay(): void;
  replay(id: string): void;
  seek(index: number): void;
  send(text?: string): void;
  toggleMic(): void;
}

export function useSignpost(): Signpost {
  const [state, dispatch] = useReducer(reducer, initialState);

  // Latest state for callbacks that outlive a render (timers, speech events).
  const latest = useRef(state);
  latest.current = state;

  const dictation = useRef<DictationSession | null>(null);
  const mounted = useRef(true);

  // Playback: one gloss token every 760 / speed ms. Re-arms on a speed change
  // from the current index; a new run restarts the cadence.
  useEffect(() => {
    if (!state.playing) return;
    const timer = setInterval(() => dispatch({ type: 'step' }), stepMs(state.speed));
    return () => clearInterval(timer);
  }, [state.playing, state.speed, state.playRun]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      dictation.current?.stop();
      dictation.current = null;
    };
  }, []);

  const send = useCallback((override?: string) => {
    const current = latest.current;
    const text = (override ?? current.draft).trim();
    if (!text || current.busy) return;

    const history = current.msgs.map(({ role, english }) => ({ role, english }));
    dispatch({ type: 'send', id: nextId('u'), text });

    void requestInterpretation({ text, dialect: current.dialect, history })
      .then((interpretation) => {
        if (mounted.current) dispatch({ type: 'reply', id: nextId('a'), interpretation });
      })
      .catch(() => {
        if (mounted.current) dispatch({ type: 'fail' });
      });
  }, []);

  const stopMic = useCallback(() => {
    dictation.current?.stop();
    dictation.current = null;
    dispatch({ type: 'listenEnd' });
  }, []);

  const toggleMic = useCallback(() => {
    if (latest.current.listening) {
      stopMic();
      return;
    }
    dictation.current = startDictation({
      onStart: (simulated) => dispatch({ type: 'listenStart', simulated }),
      onPartial: (partial) => dispatch({ type: 'listenPartial', partial }),
      onDone: (transcript) => {
        dictation.current = null;
        dispatch({ type: 'listenEnd' });
        // On end the transcript becomes the draft and sends automatically.
        if (transcript) send(transcript);
      },
    });
  }, [send, stopMic]);

  return useMemo<Signpost>(
    () => ({
      state,
      setMode: (mode) => dispatch({ type: 'setMode', mode }),
      setSpeed: (speed) => dispatch({ type: 'setSpeed', speed }),
      setDialect: (dialect) => dispatch({ type: 'setDialect', dialect }),
      setGlossScale: (scale) => dispatch({ type: 'setGlossScale', scale }),
      setEnglishOpen: (open) => dispatch({ type: 'setEnglishOpen', open }),
      toggleEnglishPanel: () => dispatch({ type: 'toggleEnglishPanel' }),
      toggleMessageEnglish: (id) => dispatch({ type: 'toggleMessageEnglish', id }),
      openSettings: () => dispatch({ type: 'setSettingsOpen', open: true }),
      closeSettings: () => dispatch({ type: 'setSettingsOpen', open: false }),
      setDraft: (draft) => dispatch({ type: 'setDraft', draft }),
      togglePlay: () => dispatch({ type: 'togglePlay' }),
      replay: (id) => dispatch({ type: 'play', id, from: 0 }),
      seek: (index) => dispatch({ type: 'seek', index }),
      send,
      toggleMic,
    }),
    [state, send, toggleMic],
  );
}
